import { PROTOCOL_VERSION, } from "@talent/protocol";
/**
 * The only production clock. `performance.now()` is monotonic; `Date.now()` is not,
 * and silently falling back to it would break the ordering guarantee this module
 * exists to provide — a device whose wall clock jumps would reorder a session's
 * events with no trace. If no monotonic clock exists, that is worth failing over.
 * Tests inject their own `Clock`.
 */
export const systemClock = {
    now: () => {
        if (typeof performance === "undefined") {
            throw new Error("no monotonic clock available; refusing to fall back to wall clock");
        }
        return performance.now();
    },
    wallIso: () => new Date().toISOString(),
};
export class SessionRecorder {
    #seq = 0;
    #t0;
    #currentTaskId = null;
    #currentTaskVersion = null;
    #inTrial = false;
    #flushing = false;
    #pendingSinceFlush = 0;
    #store;
    #transport;
    #clock;
    #newId;
    #flushEveryEvents;
    #batchSize;
    #sessionId;
    #profileId;
    #appVersion;
    #bundleVersion;
    constructor(options) {
        this.#store = options.store;
        this.#transport = options.transport;
        this.#clock = options.clock ?? systemClock;
        this.#newId = options.newId ?? (() => crypto.randomUUID());
        this.#flushEveryEvents = options.flushEveryEvents ?? 5;
        this.#batchSize = options.batchSize ?? 100;
        this.#sessionId = options.sessionId;
        this.#profileId = options.profileId;
        this.#appVersion = options.appVersion;
        this.#bundleVersion = options.pinnedBundleVersion;
        this.#t0 = this.#clock.now();
    }
    /** Task context is attached to every subsequent event until cleared. */
    enterTask(taskId, taskVersion) {
        this.#currentTaskId = taskId;
        this.#currentTaskVersion = taskVersion;
    }
    exitTask() {
        this.#currentTaskId = null;
        this.#currentTaskVersion = null;
    }
    /** Whether a scored trial is open — decides `duringTrial` on interruption events. */
    get inTrial() {
        return this.#inTrial;
    }
    setInTrial(v) {
        this.#inTrial = v;
    }
    async record(kind, payload) {
        const event = {
            eventId: this.#newId(),
            sessionId: this.#sessionId,
            profileId: this.#profileId,
            seq: this.#seq++,
            tSessionMs: Math.max(0, this.#clock.now() - this.#t0),
            tWallIso: this.#clock.wallIso(),
            appVersion: this.#appVersion,
            bundleVersion: this.#bundleVersion,
            taskId: this.#currentTaskId,
            taskVersion: this.#currentTaskVersion,
            protocolVersion: PROTOCOL_VERSION,
            kind,
            payload,
        };
        // Durable BEFORE anything else can fail. If the tab dies on the next line, the
        // event is already recoverable.
        await this.#store.put([event]);
        this.#pendingSinceFlush++;
        if (this.#pendingSinceFlush >= this.#flushEveryEvents) {
            void this.flush();
        }
    }
    /**
     * Drain the outbox. Safe to call concurrently — overlapping calls collapse.
     * Nothing is removed on transport failure, which is why ingest must be idempotent
     * on eventId: a retried batch is the normal case, not an error.
     */
    async flush() {
        if (this.#flushing)
            return;
        this.#flushing = true;
        try {
            for (;;) {
                const batch = await this.#store.peek(this.#batchSize);
                if (batch.length === 0)
                    break;
                let result;
                try {
                    result = await this.#transport.send(batch);
                }
                catch {
                    // Offline or unreachable. Keep everything queued and stop; the next flush
                    // retries. Never drop on transport failure.
                    break;
                }
                // Accepted and duplicate are both settled. Rejected events are malformed and
                // would otherwise block the queue forever, so they are dropped here — the
                // server has already recorded the rejection reason.
                if (result.rejected.length > 0) {
                    console.warn(`[telemetry] server rejected ${result.rejected.length} event(s); see server logs`);
                }
                await this.#store.remove(batch.map((e) => e.eventId));
                this.#pendingSinceFlush = 0;
                if (batch.length < this.#batchSize)
                    break;
            }
        }
        finally {
            this.#flushing = false;
        }
    }
    /**
     * Wire up flush triggers. `visibilitychange` matters most: on mobile it is the last
     * reliable moment before the OS may freeze or kill the page.
     */
    attachLifecycle(target) {
        target.addEventListener("visibilitychange", () => {
            void this.record("visibility_change", {
                visible: typeof document !== "undefined" ? !document.hidden : true,
                duringTrial: this.#inTrial,
            }).then(() => this.flush());
        });
        target.addEventListener("pagehide", () => void this.flush());
        target.addEventListener("online", () => void this.flush());
    }
    get queuedSinceFlush() {
        return this.#pendingSinceFlush;
    }
    get nextSeq() {
        return this.#seq;
    }
}
/**
 * Ask the browser to make storage persistent, and record the answer.
 * A denied grant is not a failure to hide — it changes the eviction risk for that
 * device, so it belongs in the data (ADR-002).
 */
export async function requestPersistentStorage() {
    if (typeof navigator === "undefined" || !navigator.storage) {
        return { granted: false, estimateBytes: null };
    }
    let granted = false;
    try {
        granted = navigator.storage.persist ? await navigator.storage.persist() : false;
    }
    catch {
        granted = false;
    }
    let estimateBytes = null;
    try {
        const est = navigator.storage.estimate ? await navigator.storage.estimate() : null;
        estimateBytes = est?.quota ?? null;
    }
    catch {
        estimateBytes = null;
    }
    return { granted, estimateBytes };
}
//# sourceMappingURL=recorder.js.map